// Labor and parts on an operation (moved out of app.js, docs/split-plan.md session 5): the Add labor
// and Add Part composers, the old Add Part popup chain, saving entries (and the job's device copy),
// taking a part off the shelf (reduceInventory), deleting, the Labor & Parts sheet with its inline
// edits and live updates, and adding a parts kit to the job. app.js helpers come in through
// setLaborPartsDeps.
import { formatYMDLocal } from './dailyLog';
import { database } from './initFirebase';
import { remove, set } from './trackedDb';
import { escapeHTML, fetchIdToken, generateId, getDB, toastMessage, updateTimestamps } from './utilities';
import { ref } from 'firebase/database';

let shell = null;
export function setLaborPartsDeps(deps) { shell = deps; }

// A submit button's handler that ignores more clicks (and a held-down Enter) until the first
// finishes: a double tap must not log the same labor or part twice.
function oneAtATime(fn) {
  let running = false;
  return async function (...args) {
    if (running) return;
    running = true;
    const btn = args[0]?.currentTarget;
    if (btn) btn.disabled = true;
    try { return await fn.apply(this, args); } finally { running = false; if (btn) btn.disabled = false; }
  };
}

document.getElementById('open-add-labor').addEventListener('click', () => {
  document.getElementById('labor-entry-date').value = formatYMDLocal(new Date());
  document.getElementById('labor-entry-description').innerHTML = '';
  document.getElementById('labor-entry-hours').value = '';
  document.getElementById('add-labor').textContent = 'Save entry';
  document.getElementById('labor-add-composer').style.display = '';
  document.getElementById('labor-entry-tech').focus();
});

document.getElementById('labor-add-cancel').addEventListener('click', () => {
  document.getElementById('labor-add-composer').style.display = 'none';
});

document.getElementById('open-add-parts').addEventListener('click', () => {
  openPartAddComposer();
});

document.getElementById('part-add-cancel').addEventListener('click', () => {
  closePartAddComposer();
});

document.getElementById('add-labor').addEventListener('click', oneAtATime(submitLabor));

// ---------------------------------------------------------------------------
// Add Part — inline composer (replaces the old #open-add-parts popup chain).
// Single part per submit, same shape/handlers as the per-row parts autosave:
// inventory lookup (typeahead over the same inventoryData IndexedDB store the
// Parts Kit picker reads via getPartById), manual entry when nothing matches,
// or a first-class Misc-charge path (description + $ amount, no part number).
// ---------------------------------------------------------------------------
const partAddState = { selected: null, misc: false };

function openPartAddComposer() {
  partAddState.selected = null;
  partAddState.misc = false;

  document.getElementById('part-add-search').value = '';
  document.getElementById('part-add-results').style.display = 'none';
  document.getElementById('part-add-results').innerHTML = '';
  document.getElementById('part-add-manual-hint').style.display = 'none';
  document.getElementById('part-add-misc-toggle').classList.remove('active');
  document.getElementById('part-add-fields').style.display = 'none';
  document.getElementById('part-add-description').value = '';
  document.getElementById('part-add-qty').value = '1';
  document.getElementById('part-add-date').value = formatYMDLocal(new Date());
  document.getElementById('part-add-desc-label').textContent = 'Description';
  document.getElementById('part-add-amount-label').textContent = 'Qty';

  document.getElementById('part-add-composer').style.display = '';
  document.getElementById('part-add-search').focus();
}

function closePartAddComposer() {
  document.getElementById('part-add-composer').style.display = 'none';
}

function renderPartAddResults(matches) {
  const results = document.getElementById('part-add-results');
  const hint = document.getElementById('part-add-manual-hint');
  const query = document.getElementById('part-add-search').value.trim();

  if (!matches.length) {
    results.style.display = 'none';
    results.innerHTML = '';
    hint.style.display = query ? '' : 'none';
    return;
  }

  hint.style.display = 'none';
  results.innerHTML = '';
  matches.slice(0, 8).forEach(item => {
    const onHand = Array.isArray(item.Bins)
      ? item.Bins.reduce((s, b) => s + (Number(b.qty) || 0), 0)
      : (Number(item.Qty) || 0);
    const min = Number(item.Min) || 0;
    const stockClass = onHand === 0 ? 'stock-out' : (onHand <= min ? 'stock-low' : 'stock-ok');

    const row = document.createElement('div');
    row.className = 'op-composer-result-row';
    row.innerHTML = `
      <span class="op-composer-result-num">${escapeHTML(item.Part_Number || '')}</span>
      <span class="op-composer-result-desc">${escapeHTML(item.Description || '')}</span>
      <span class="op-composer-result-stock ${stockClass}">${onHand} in stock</span>
    `;
    row.addEventListener('click', () => selectPartAddResult(item));
    results.appendChild(row);
  });
  results.style.display = '';
}

function selectPartAddResult(item) {
  partAddState.selected = { Part_Number: item.Part_Number || '', Description: item.Description || '' };
  partAddState.misc = false;
  document.getElementById('part-add-search').value = item.Part_Number || '';
  document.getElementById('part-add-results').style.display = 'none';
  document.getElementById('part-add-manual-hint').style.display = 'none';
  document.getElementById('part-add-misc-toggle').classList.remove('active');
  document.getElementById('part-add-desc-label').textContent = 'Description';
  document.getElementById('part-add-amount-label').textContent = 'Qty';
  document.getElementById('part-add-description').value = item.Description || '';
  document.getElementById('part-add-fields').style.display = '';
  document.getElementById('part-add-qty').focus();
}

function useTypedPartAsManual() {
  const query = document.getElementById('part-add-search').value.trim();
  if (!query) return;
  partAddState.selected = { Part_Number: query, Description: '' };
  partAddState.misc = false;
  document.getElementById('part-add-results').style.display = 'none';
  document.getElementById('part-add-desc-label').textContent = 'Description';
  document.getElementById('part-add-amount-label').textContent = 'Qty';
  document.getElementById('part-add-fields').style.display = '';
  document.getElementById('part-add-description').focus();
}

document.getElementById('part-add-search').addEventListener('input', async (e) => {
  const query = e.target.value.toLowerCase().trim();
  if (!query) {
    renderPartAddResults([]);
    return;
  }
  try {
    const inventoryData = await shell.fetchDataFromStore('inventoryData');
    const matches = inventoryData.filter(item =>
      (item.Part_Number || '').toString().toLowerCase().includes(query) ||
      (item.Description || '').toString().toLowerCase().includes(query)
    );
    renderPartAddResults(matches);
  } catch (err) {
    console.error('Part lookup failed:', err);
  }
});

document.getElementById('part-add-search').addEventListener('keydown', (e) => {
  if (e.key !== 'Enter') return;
  const firstResult = document.querySelector('#part-add-results .op-composer-result-row');
  if (firstResult) {
    firstResult.click();
  } else {
    useTypedPartAsManual();
  }
});

document.getElementById('part-add-misc-toggle').addEventListener('click', () => {
  partAddState.selected = null;
  partAddState.misc = true;
  document.getElementById('part-add-results').style.display = 'none';
  document.getElementById('part-add-manual-hint').style.display = 'none';
  document.getElementById('part-add-misc-toggle').classList.add('active');
  document.getElementById('part-add-search').value = '';
  document.getElementById('part-add-desc-label').textContent = 'Description';
  document.getElementById('part-add-amount-label').textContent = 'Amount $';
  document.getElementById('part-add-description').value = '';
  document.getElementById('part-add-qty').value = '';
  document.getElementById('part-add-fields').style.display = '';
  document.getElementById('part-add-description').focus();
});

document.getElementById('part-add-save').addEventListener('click', oneAtATime(async () => {
  try {
    const description = document.getElementById('part-add-description').value.trim();
    const qtyOrAmount = document.getElementById('part-add-qty').value.trim();
    const tech = document.getElementById('part-add-tech').value;
    const entryDate = document.getElementById('part-add-date').value;
    const jobId = document.getElementById('currentJobId').value;

    let partNumber, quantity;
    if (partAddState.misc) {
      if (!description || !qtyOrAmount) {
        toastMessage('Description and amount are required for a misc charge', 'red');
        return;
      }
      partNumber = `${description} $${qtyOrAmount}`;
      quantity = '1';
    } else {
      const typedNumber = document.getElementById('part-add-search').value.trim();
      partNumber = (partAddState.selected?.Part_Number || typedNumber);
      if (!partNumber) {
        toastMessage('Enter a part number or description', 'red');
        return;
      }
      quantity = qtyOrAmount || '1';
    }

    const date = await shell.formatDate(entryDate);
    const partData = {
      Part_Number: partNumber,
      Description: partAddState.misc ? '' : description,
      Quantity: quantity,
      Category: document.getElementById('currentCategory').value,
      Op_Type: document.getElementById('currentOpType').value,
      Job_ID: jobId,
      ID: await generateId(),
      Boat_Name: document.getElementById('currentBoat').value,
      Customer_Name: document.getElementById('currentName').value,
      Time_Submitted: date,
      Work_Order: document.getElementById('currentWorkOrder').value,
      Tech: tech
    };

    await addPartsFirebase(partData);
    await updatePartsIndex(jobId, partData);
    updateTimestamps('jobData', 'data');
    toastMessage('Part Submitted', 'green');
    generatePartsTable(jobId);
    closePartAddComposer();
  } catch (err) {
    console.error('Failed to submit part', err);
    toastMessage('Failed to submit part', 'red');
  }
}));

document.getElementById('labor-entry-hours').addEventListener('input', (e) => {
  const hrs = parseFloat(e.target.value);
  document.getElementById('add-labor').textContent = hrs > 0 ? `Log ${hrs} hrs` : 'Log time';
});

document.getElementById('add-part').addEventListener('click', () => {
  addParts();
});

document.getElementById('save-parts').addEventListener('click', () => {
  document.getElementById('parts-section').style.display = 'none'; 
});

export async function fetchParts() {
  try {
    const db = await getDB();

    const transaction = db.transaction('inventoryData', 'readonly');
    const store = transaction.objectStore('inventoryData');

    const data = await new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = event => resolve(request.result);
      request.onerror = event => reject(request.error);
    });

    shell.partsList(data);

  } catch (error) {
    console.error('Failed to fetch data from IndexedDB:', error);
  }
}

function formatCurrency(value) {
  // Convert string to a number if it's not already
  const numberValue = typeof value === "string" ? parseFloat(value) : value;

  // Use Intl.NumberFormat to format the number as currency
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    // You can adjust the options as needed
  });

  return formatter.format(numberValue);
}

export async function addLaborFirebase(laborData) {
console.log(laborData);
  // No operation open: 'data/' + '' + '/Labor/x' would land in /data/Labor itself.
  if (!laborData.Job_ID) throw new Error('No operation is open; labor not saved');
  try {
    const laborRef = ref(database, 'data/' + laborData.Job_ID + '/Labor/' + laborData.ID);

    console.log(laborRef);

    // Not awaited: offline, the SDK only settles once reconnected (trackedDb keeps it durable).
    set(laborRef, laborData).catch((error) => {
      console.error('Error adding labor record:', error);
      toastMessage('Labor entry did not save to the server. Please try again.', 'red');
    });
  } catch (error) {
    console.error('Failed to add labor to Firebase', error);
  }

}

async function submitLabor() {

  try {
    const originalText = document.getElementById('labor-entry-description').value;
    const labor = originalText.replace(/&nbsp;/g, '');

    let techSelector;

    if (document.getElementById('labor-entry-tech').value === '' || document.getElementById('labor-entry-tech').value === 'default') {
      const userInfo = JSON.parse(localStorage.getItem('userInfo'));
      const currentTech = userInfo.displayName.split(' ')[0];
      techSelector = currentTech;
    } else {
      techSelector = document.getElementById('labor-entry-tech').value;
    }

    const jobId = document.getElementById('currentJobId').value;
    const entryId = await generateId();
    const entryDate = document.getElementById('labor-entry-date').value;
    const date = await shell.formatDate(entryDate);
    const category = document.getElementById('currentCategory').value;
    const opType = document.getElementById('currentOpType').value;
    const status = document.getElementById('currentStatus').value;
    const assignedTech = document.getElementById('currentTech').value;


    const laborData = {
      'Description': labor,
      'Hours': document.getElementById('labor-entry-hours').value,
      'Category': category,
      'Op_Type': opType,
      'Job_ID': jobId,
      'ID': entryId,
      'Boat_Name': document.getElementById('currentBoat').value,
      'Customer_Name': document.getElementById('currentName').value,
      'Time_Submitted': date,
      'Work_Order': document.getElementById('currentWorkOrder').value,
      'Tech': techSelector
    }

    await addLaborFirebase(laborData);
    await updateLaborIndex(jobId, laborData);
    updateTimestamps('jobData', 'data');

    if (status == 'On Work Schedule') {

      shell.updateJobStatus(jobId, 'In Progress')

      if(shell.selectedJobRow){
        shell.selectedJobRow.style.backgroundColor = 'rgb(236, 237, 175)';
      }

      if(shell.activeTableRow){
        { const pill = shell.activeTableRow.querySelector('.status-info'); if (pill) pill.style.backgroundColor = 'yellow'; }
      }
    
    };
    toastMessage('Labor Submitted', 'green');
    document.getElementById('labor-entry-description').value = '';
    document.getElementById('labor-entry-hours').value = '';
    generateLaborTable(jobId);
    document.getElementById('labor-add-composer').style.display = 'none';
  } catch (error) {
    console.error('Failed to submit labor', error);
    toastMessage('Failed to submit labor', 'red');
  }

}

export async function getTodaysDate() {
  const today = new Date();
  const year = today.getFullYear().toString();
  const month = (today.getMonth() + 1).toString().padStart(2, '0');
  const date = today.getDate().toString().padStart(2, '0');
  return year + '-' + month + '-' + date;
}

function initializePartsTable() {
  const tbody = document.getElementById("parts-table-body");
  tbody.innerHTML = '';
}

export function addParts(quick) {

  const jobId = document.getElementById('currentJobId').value;

  // Create and show the part number input popup
  if(quick !== undefined && quick !== null && quick !== '') {
    const partNumber = quick.split('(')[0].trim();
    let description;

    if(quick.includes('(')){
    description = quick.split('(')[1].split(')')[0].trim();
    } else {
      description = partNumber;
    }

    showQuantityPopup(partNumber, description)
    return
  }
  const partPopup = createPopup('Enter Part Number');
  const partInput = document.createElement('input');
  partInput.type = 'text';
  partInput.placeholder = 'Part Number';
  partInput.className = 'new-part-input';

  partInput.addEventListener('keydown',async (e) => {
    if (e.key === 'Enter') {
    const partNumber = partInput.value.trim();
    if (partNumber !== '') {
      closePopup(partPopup);
      const description = await showDescriptionPopup(partNumber);
      showQuantityPopup(partNumber, description);;
    }
  }
  });

  if (typeof quick === 'string') {
    partInput.value = quick;
  }
  partPopup.appendChild(partInput);

  const suggestionContainer = document.createElement('div');
  suggestionContainer.classList.add('suggestion-list');
  suggestionContainer.style.display = 'none';

  const submitButton = document.createElement('button');
  submitButton.textContent = 'Submit';
  submitButton.classList.add('pop-up-submit-button');

  submitButton.addEventListener('click',async () => {
    const partNumber = partInput.value.trim();
    if (partNumber !== '') {
      closePopup(partPopup);
      const description = await showDescriptionPopup(partNumber);
      showQuantityPopup(partNumber, description);;
    }
  });

  const cancelButton = document.createElement('button');
  cancelButton.textContent = 'Cancel';
  cancelButton.classList.add('pop-up-cancel-button');
  cancelButton.addEventListener('click', () => {
    closePopup(partPopup);
  });

  partPopup.appendChild(submitButton);
  partPopup.appendChild(cancelButton);

  partPopup.appendChild(suggestionContainer);

  partInput.addEventListener('input', async function () {
    try {
      const query = partInput.value.toLowerCase().trim();
      suggestionContainer.innerHTML = '';

      let inventoryData = await shell.fetchDataFromStore('inventoryData');
      const filteredData = inventoryData.filter(item => {
        const itemValues = Object.values(item).map(value => value.toString().toLowerCase());
        return query.split(' ').every(term => itemValues.some(value => value.includes(term)));
      });

      filteredData.forEach(item => {
        const suggestionItem = document.createElement('div');
        suggestionItem.classList.add('suggestion-item');
        suggestionItem.textContent = `${item.Part_Number} (${item.Description})`;
        suggestionItem.addEventListener('click', () => {
          console.log(item);
          partInput.value = `${item.Part_Number} (${item.Description})`;
          suggestionContainer.style.display = 'none';
          closePopup(partPopup);
          showQuantityPopup(item.Part_Number, item.Description, item.id);
        });
        suggestionContainer.appendChild(suggestionItem);
      });

      suggestionContainer.style.display = filteredData.length ? 'flex' : 'none';
    } catch (error) {
      console.error('Failed to fetch data from IndexedDB:', error);
    }
  });

  document.body.appendChild(partPopup);
  partInput.focus();
}

export async function showDescriptionPopup(partNumber) {
  return new Promise((resolve) => {
    const descPopup = createPopup(`Enter Description for ${partNumber}`);
    const descInput = document.createElement('input');
    descInput.type = 'text';
    descInput.placeholder = 'Description';
    descInput.className = 'new-desc-input';

    descInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
      const description = descInput.value.trim();
      if (description !== '') {
        closePopup(descPopup);
        resolve(description); // Return the entered description
      }
    }
    });

    descPopup.appendChild(descInput);

    const confirmButton = document.createElement('button');
    confirmButton.textContent = 'Submit';
    confirmButton.classList.add('pop-up-submit-button')

    confirmButton.addEventListener('click', () => {
      const description = descInput.value.trim();
      if (description !== '') {
        closePopup(descPopup);
        resolve(description); // Return the entered description
      }
    });

    const cancelButton = document.createElement('button');
    cancelButton.textContent = 'Cancel';
    cancelButton.classList.add('pop-up-cancel-button');
    cancelButton.addEventListener('click', () => {
      closePopup(descPopup);
      resolve(''); // Return an empty string
    });

    descPopup.appendChild(confirmButton);
    descPopup.appendChild(cancelButton);

    document.body.appendChild(descPopup);
    descInput.focus();
  });
}


async function showQuantityPopup(partNumber, description, partId) {
  // If the description is blank, ask the user for it
  if (!description || description.trim() === '') {
    description = await showDescriptionPopup(partNumber);
  }

  const qtyPopup = createPopup('Enter Quantity');
  const qtyInput = document.createElement('input');

  qtyInput.type = 'number';
  qtyInput.placeholder = 'Quantity';
  qtyInput.className = 'new-qty-input';

  qtyInput.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
    const quantity = qtyInput.value.trim();
    if (quantity !== '') {
      addPartToTable(partNumber, description, quantity);
      reduceInventory(partId, quantity);
      closePopup(qtyPopup);
    }
  }
  });

  qtyPopup.appendChild(qtyInput);


  const confirmButton = document.createElement('button');
  confirmButton.textContent = 'Submit';
  confirmButton.classList.add('pop-up-submit-button');

  confirmButton.addEventListener('click', () => {
    const quantity = qtyInput.value.trim();
    if (quantity !== '') {
      addPartToTable(partNumber, description, quantity);
      reduceInventory(partId, quantity);
      closePopup(qtyPopup);
    }
  });

  const cancelButton = document.createElement('button');
  cancelButton.textContent = 'Cancel';
  cancelButton.classList.add('pop-up-cancel-button');
  cancelButton.addEventListener('click', () => {
    closePopup(qtyPopup);
  });
  

  qtyPopup.appendChild(confirmButton);
  qtyPopup.appendChild(cancelButton);

  document.body.appendChild(qtyPopup);
  qtyInput.focus();
}

async function addPartToTable(partNumber, description, quantity) {
const today = await getTodaysDate();

  try {
    const tbody = document.getElementById("parts-table-body");
    const row = document.createElement("div");
    row.className = "parts-data-row";

    const id = document.createElement('p');
    id.textContent = await generateId();
    id.className = "id-selector";
    id.style.display = "none";

    row.setAttribute('data-id', id.textContent);

    const part = document.createElement('div');
    part.textContent = `${partNumber} (${description})`;
    part.className = "parts-selector";

    const qtyContainer = document.createElement('div');
    qtyContainer.className = 'qty-container';

    const qty = document.createElement('div');
    qty.textContent = quantity;
    qty.className = "qty-selector";

    qtyContainer.appendChild(qty);

    const del = document.createElement('button');
    del.className = 'parts-data-delete-button table-button';
    del.innerHTML = '<img src="/assets/close_icon.png" alt="Close Icon" width="24" height="24">';
    del.addEventListener('click', deleteParts);

    row.appendChild(id);
    row.appendChild(part);
    row.appendChild(qtyContainer);
    row.appendChild(del);

    tbody.appendChild(row);

    // Add part to Firebase and update timestamps
    const partData = {
      'Part_Number': partNumber,
      'Quantity': quantity,
      'Category': document.getElementById('currentCategory').value,
      'Op_Type': document.getElementById('currentOpType').value,
      'Job_ID': document.getElementById('currentJobId').value,
      'ID': id.textContent,
      'Boat_Name': document.getElementById('currentBoat').value,
      'Customer_Name': document.getElementById('currentName').value,
      'Time_Submitted': today,
      'Work_Order': document.getElementById('currentWorkOrder').value,
      'Tech': getTechSelector(),
      'Description': description
    };

    await addPartsFirebase(partData);
    await updatePartsIndex(document.getElementById('currentJobId').value, partData);
    updateTimestamps('jobData', 'data');
    generatePartsTable(document.getElementById('currentJobId').value);
  } catch (error) {
    console.error('Failed to add part to table:', error);
  }

}

export function createPopup(title) {
  const popup = document.createElement('div');
  popup.className = 'part-popup';
  popup.style.position = 'fixed';
  popup.style.left = '50%';
  popup.style.top = '25%';
  popup.style.transform = 'translate(-50%, -50%)';
  popup.style.backgroundColor = 'white';
  popup.style.padding = '1rem';
  popup.style.boxShadow = 'rgba(0, 0, 0, 0.5) 0px 0px 1000px 1000px';
  popup.style.zIndex = '2000';
  popup.style.borderRadius = '10px';
  popup.style.display = 'flex';
  popup.style.flexDirection = 'column';

  const titleElement = document.createElement('h3');
  titleElement.textContent = title;

  const submitButton = document.createElement('button');
  submitButton.textContent = 'Submit';
  submitButton.classList.add('pop-up-submit-button');
  submitButton.addEventListener('click', () => {
    closePopup(popup);
  });

  const cancelButton = document.createElement('button');
  cancelButton.textContent = 'Cancel';
  cancelButton.classList.add('pop-up-cancel-button');
  cancelButton.addEventListener('click', () => {
    closePopup(popup);
  });

  popup.appendChild(titleElement);

  return popup;
}

export function closePopup(popup) {
  document.body.removeChild(popup);
}

// Deducts quantityChange from a part's stock on work-order/estimate
// conversion. Inventory redesign (design_handoff_inventory_fuel_dashboard)
// added per-bin stock (item.Bins) and a Usage_History log the new Inventory
// page's detail panel reads from — this now deducts from the first bin with
// stock (else bin 0), keeps Qty in sync with sum(Bins), records a usage
// entry, and writes through to Firebase (previously IndexedDB-only, which
// meant the next Firebase→IDB sync silently reverted every WO-driven
// deduction — see adjustInventoryQuantity in utilities.js for the pattern
// this now matches).
export async function reduceInventory(partId, quantityChange, ref, who) {
  console.log(partId, quantityChange);
  try {
      const db = await getDB();
      const transaction = db.transaction('inventoryData', 'readwrite');
      const store = transaction.objectStore('inventoryData');

      const request = store.get(partId);
      const inventoryItem = await new Promise((resolve, reject) => {
          request.onsuccess = (event) => {
              resolve(event.target.result);
          };
          request.onerror = (event) => {
              reject(new Error('Unable to retrieve part data'));
          };
      });

      if (!inventoryItem) {
          throw new Error(`Part with ID ${partId} not found`);
      }

      const bins = Array.isArray(inventoryItem.Bins) && inventoryItem.Bins.length
        ? inventoryItem.Bins.map(b => ({ ...b }))
        : [{ name: inventoryItem.Location || 'Unassigned', qty: Number(inventoryItem.Qty) || 0 }];

      if (quantityChange > 0) {
        const idx = bins.findIndex(b => b.qty > 0) >= 0 ? bins.findIndex(b => b.qty > 0) : 0;
        bins[idx].qty = Math.max(0, bins[idx].qty - quantityChange);
      }

      const updatedQty = bins.reduce((s, b) => s + (Number(b.qty) || 0), 0);
      const usageEntry = {
        date: new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        ref: ref || 'Work order',
        qty: quantityChange,
        who: who || 'Unknown'
      };

      inventoryItem.Bins = bins;
      inventoryItem.Qty = updatedQty;
      inventoryItem.Location = bins[0].name;
      inventoryItem.Usage_History = [usageEntry, ...(inventoryItem.Usage_History || [])];

      const updateRequest = store.put(inventoryItem);
      await new Promise((resolve, reject) => {
          updateRequest.onsuccess = () => resolve();
          updateRequest.onerror = () => reject(new Error('Unable to update inventory'));
      });

      const idToken = await fetchIdToken();
      const partUrl = `${shell.currentURL}/inventory/${partId}.json?auth=${idToken}`;
      const response = await fetch(partUrl, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          Bins: bins, Qty: updatedQty, Location: bins[0].name,
          Usage_History: inventoryItem.Usage_History
        })
      });
      if (!response.ok) throw new Error(`Firebase update failed with status ${response.status}`);
      await updateTimestamps('inventoryData', 'inventory');

      console.log(`Inventory for part ${partId} updated. New quantity: ${updatedQty}`);
  } catch (error) {
      console.error('Error updating inventory:', error);
  }
}

function getTechSelector() {
  const techSelect = document.getElementById('part-entry-tech');
  if (techSelect.value === '' || techSelect.value === 'default') {
    const userInfo = JSON.parse(localStorage.getItem('userInfo'));
    return userInfo.displayName.split(' ')[0];
  }
  return techSelect.value;
}

export async function addPartsFirebase(partData) {
console.log(partData);
  if (!partData.Job_ID) throw new Error('No operation is open; part not saved'); // see addLaborFirebase
  try {
    const partRef = ref(database, 'data/' + partData.Job_ID + '/Parts/' + partData.ID);

    // Not awaited: offline, the SDK only settles once reconnected (trackedDb keeps it durable).
    set(partRef, partData).catch((error) => {
      console.error('Error adding part record:', error);
      toastMessage('Part did not save to the server. Please try again.', 'red');
    });
  } catch (error) {
    console.error('Failed to add part to Firebase:', error);
  }

}

export async function getJobData(jobId) {
  try {
    const db = await getDB();
    const transaction = db.transaction(['jobData'], 'readonly');
    const objectStore = transaction.objectStore('jobData');

    return await new Promise((resolve, reject) => {
      const req = objectStore.get(jobId);

      req.onerror = (event) => {
        reject(new Error(`Error getting job data: ${event.target.errorCode || req.error}`));
      };

      req.onsuccess = () => {
        resolve(req.result || null);
      };
    });
  } catch (error) {
    console.error('getJobData:', error);
    throw error;
  }
}


export async function updateLaborIndex(jobId, laborEntry) {
  try {
    const db = await getDB();
    const transaction = db.transaction(['jobData'], 'readwrite');
    const objectStore = transaction.objectStore('jobData');

    const jobData = await new Promise((resolve, reject) => {
      const req = objectStore.get(jobId);
      req.onerror = (event) => reject(new Error(`Error fetching job data: ${event.target.errorCode || req.error}`));
      req.onsuccess = () => resolve(req.result || null);
    });

    if (jobData) {
      const laborId = laborEntry.ID;

      if (!jobData.Labor) jobData.Labor = {};

      jobData.Labor[laborId] = laborEntry;

      await new Promise((resolve, reject) => {
        const req = objectStore.put(jobData);
        req.onerror = (event) => reject(new Error(`Error updating job data: ${event.target.errorCode || req.error}`));
        req.onsuccess = () => resolve();
      });

      console.log('Job data updated successfully');
    } else {
      console.error('Job not found:', jobId);
    }
  } catch (error) {
    console.error('Failed to update labor index:', error);
  }
}


async function removeLaborFromIndex(jobId, laborId) {
  try {
    const db = await getDB();
    const transaction = db.transaction(['jobData'], 'readwrite');
    const objectStore = transaction.objectStore('jobData');

    const jobData = await new Promise((resolve, reject) => {
      const req = objectStore.get(jobId);
      req.onerror = (event) =>
        reject(new Error(`Error fetching job data: ${event.target.errorCode || req.error}`));
      req.onsuccess = () => resolve(req.result || null);
    });

    if (jobData && jobData.Labor && jobData.Labor[laborId]) {
      delete jobData.Labor[laborId];

      await new Promise((resolve, reject) => {
        const req = objectStore.put(jobData);
        req.onerror = (event) =>
          reject(new Error(`Error updating job data: ${event.target.errorCode || req.error}`));
        req.onsuccess = () => resolve();
      });

      console.log('Labor entry removed successfully');
    } // else already gone: the live listener can drop it from the device copy first
  } catch (error) {
    console.error('Error removing labor entry:', error);
  }
}


async function updatePartsIndex(jobId, partEntry) {
  return new Promise(async (resolve, reject) => {
    try {
      const db = await getDB();
      const transaction = db.transaction(['jobData'], 'readwrite');
      const objectStore = transaction.objectStore('jobData');

      const jobData = await new Promise((res, rej) => {
        const req = objectStore.get(jobId);
        req.onerror = (event) => {
          console.error('Error fetching job data:', event.target.errorCode);
          rej(event.target.errorCode);
        };
        req.onsuccess = () => res(req.result || null);
      });

      if (jobData) {

        if (!partEntry || !partEntry.ID) {
          console.error('Part entry missing ID');
          reject('Part entry missing ID');
          return;
        }

        const partId = partEntry.ID;

        if (!jobData.Parts) jobData.Parts = {};
        jobData.Parts[partId] = partEntry;

        await new Promise((res, rej) => {
          const req = objectStore.put(jobData);
          req.onerror = (event) => {
            console.error('Error updating job data:', event.target.errorCode);
            rej(event.target.errorCode);
          };
          req.onsuccess = () => res();
        });

        console.log('Job data updated successfully');
        resolve();
      } else {
        console.error('Job not found:', jobId);
        reject('Job not found');
      }
    } catch (error) {
      console.error('Failed to update parts index:', error);
      reject(error);
    }
  });
}


async function removePartsFromIndex(jobId, partId) {
  try {
    const db = await getDB();
    const transaction = db.transaction(['jobData'], 'readwrite');
    const objectStore = transaction.objectStore('jobData');

    const jobData = await new Promise((resolve, reject) => {
      const req = objectStore.get(jobId);
      req.onerror = (event) => {
        console.error('Error fetching job data:', event.target.errorCode);
        reject(event.target.errorCode);
      };
      req.onsuccess = () => resolve(req.result || null);
    });

    if (jobData && jobData.Parts && jobData.Parts[partId]) {
      delete jobData.Parts[partId];

      await new Promise((resolve, reject) => {
        const req = objectStore.put(jobData);
        req.onerror = (event) => {
          console.error('Error updating job data:', event.target.errorCode);
          reject(event.target.errorCode);
        };
        req.onsuccess = () => resolve();
      });

      console.log('Part entry removed successfully');
    } // else already gone (see removeLaborFromIndex)
  } catch (error) {
    console.error('Error removing part entry:', error);
  }
}


function deleteLabor(e) {
  const button = e.target.closest('button');
  if (button && button.classList.contains('labor-data-delete-button')) {
    const laborEntry = button.closest('.labor-entry');
    if (laborEntry) {
      const jobId = document.getElementById('currentJobId').value;
      const laborId = laborEntry.getAttribute('data-id');

      const confirmDelete = confirm('Are you sure you want to delete this labor record?');
      if (confirmDelete) {
        toastMessage('Deleting Labor Record', 'blue');
        const laborRef = ref(database, 'data/' + jobId + '/Labor/' + laborId);
        remove(laborRef)
          .then(() => {
            toastMessage("Labor record deleted successfully", 'green');
            laborEntry.remove();
            removeLaborFromIndex(jobId, laborId);
            updateLaborCount();
            updateTimestamps('jobData', 'data');
          })
          .catch((error) => {
            toastMessage("Failed to delete labor record", 'red');
          });
      }
    }
  }
}

function deleteParts(e) {

  const button = e.target.closest('button');
  if (button && button.classList.contains('parts-data-delete-button')) {
    const partEntry = button.closest('.part-entry') ? button.closest('.part-entry') : button.closest('.parts-data-row');
    if (partEntry) {
      const jobId = document.getElementById('currentJobId').value;
      const partId = partEntry.getAttribute('data-id');

      const confirmDelete = confirm('Are you sure you want to delete this part record?');
      if (confirmDelete) {
        toastMessage('Deleting Parts Record', 'blue');
        const partsRef = ref(database, 'data/' + jobId + '/Parts/' + partId);
        remove(partsRef)
          .then(() => {
            toastMessage("Part record deleted successfully", 'green');
            partEntry.remove();
            removePartsFromIndex(jobId, partId);
            updatePartsCount();
            updateTimestamps('jobData', 'data');

            if(partEntry === button.closest('.parts-data-row')) {
            generatePartsTable(document.getElementById('currentJobId').value);
            }
          })
          .catch((error) => {
            toastMessage("Failed to delete part record", 'red');
          });
      }
    }
  }
}

// Recomputed from the DOM (not refetched from IndexedDB) so a delete can update
// the summary/footer/context-strip totals immediately without a full re-render.
export function laborSummaryFromDOM() {
  const entries = Array.from(document.querySelectorAll('#labor-table-body-current .labor-entry:not(.header)'));
  const perTech = new Map();
  let totalHours = 0;

  entries.forEach(row => {
    const tech = row.querySelector('.labor-tech .labor-data')?.textContent?.trim() || '';
    const hours = parseFloat(row.querySelector('.labor-data.hours')?.textContent) || 0;
    totalHours += hours;
    if (tech) perTech.set(tech, (perTech.get(tech) || 0) + hours);
  });

  const perTechText = Array.from(perTech.entries())
    .map(([tech, hrs]) => `${tech} ${hrs.toFixed(1)}`)
    .join(' · ');

  return { count: entries.length, totalHours, perTechText };
}

function updateLaborCount() {
  const { count, totalHours } = laborSummaryFromDOM();
  const summaryEl = document.getElementById('labor-header');
  if (summaryEl) summaryEl.textContent = `${count} ${count === 1 ? 'entry' : 'entries'} · ${totalHours.toFixed(1)} hrs`;
  const totalEl = document.getElementById('opContextHoursTotal');
  if (totalEl) totalEl.textContent = totalHours.toFixed(1);
  const tabCountEl = document.getElementById('opDetailLaborTabCount');
  if (tabCountEl) tabCountEl.textContent = `${count} · ${totalHours.toFixed(1)}h`;
  updateLaborFooter();
}

function updateLaborFooter() {
  const footer = document.getElementById('op-card-labor-footer');
  const { count, totalHours, perTechText } = laborSummaryFromDOM();
  if (!footer) return;
  if (count === 0) {
    footer.style.display = 'none';
    return;
  }
  footer.style.display = '';
  document.getElementById('op-card-labor-footer-total').textContent = `${totalHours.toFixed(1)} hrs`;
  document.getElementById('op-card-labor-footer-split').textContent = perTechText;
}

function updatePartsCount() {
  const partEntries = document.querySelectorAll('#parts-table-body-current .part-entry:not(.header)');
  const summaryEl = document.getElementById('parts-header');
  if (summaryEl) summaryEl.textContent = `${partEntries.length} ${partEntries.length === 1 ? 'item' : 'items'}`;
  const totalEl = document.getElementById('opContextPartsTotal');
  if (totalEl) totalEl.textContent = partEntries.length;
  const tabCountEl = document.getElementById('opDetailPartsTabCount');
  if (tabCountEl) tabCountEl.textContent = partEntries.length;
}

export async function showStatus() {

  try {

      var id = document.getElementById('currentJobId').value

      await generateLaborTable(id)
      await generatePartsTable(id)

      document.getElementById('statusTable').style.display = "flex"

  } catch (error) {
    console.error('Failed to fetch op data:', error);
  }
}

export async function addPartsKitToJob(kit) {

    for (const part of kit) {

      const partNumber = part.partNumber;
      const description = part.description;
      const quantity = part.qty;
      const today = await getTodaysDate();
      const id = part.id;

      const partData = {
        'Part_Number': partNumber,
        'Quantity': quantity,
        'Category': document.getElementById('currentCategory').value,
        'Op_Type': document.getElementById('currentOpType').value,
        'Job_ID': document.getElementById('currentJobId').value,
        'ID': id,
        'Boat_Name': document.getElementById('currentBoat').value,
        'Customer_Name': document.getElementById('currentName').value,
        'Time_Submitted': today,
        'Work_Order': document.getElementById('currentWorkOrder').value,
        'Tech': getTechSelector(),
        'Description': description
      };

      await addPartsFirebase(partData);
      await updatePartsIndex(document.getElementById('currentJobId').value, partData);
      await reduceInventory(id, quantity);
    }

    await updateTimestamps('jobData', 'data');
    await generatePartsTable(document.getElementById('currentJobId').value);
}

// Live (docs/live-updates-plan.md): the open Labor & Parts sheet follows other users' entries.
// Each entry row is compared on its own: an unchanged one is left alone, a changed one is rebuilt
// (unless it's being typed in or waiting to save), a new one is slotted in by time, a deleted one
// goes. The sheet is never cleared and redrawn.
function syncEntryRows(container, entries, rowClass, build, newestFirst) {
  if (!container) return [];
  const byId = new Map(entries.filter(e => e && e.ID).map(e => [String(e.ID), e]));
  const order = [...byId.values()].sort((a, b) => (new Date(a.Time_Submitted) - new Date(b.Time_Submitted)) * (newestFirst ? -1 : 1));
  const latestId = order[0] ? String(order[0].ID) : null;
  const busy = (row) => row.contains(document.activeElement) || laborSaveTimers.has(row.getAttribute('data-id'));
  const changed = [];
  container.querySelectorAll(`.${rowClass}`).forEach(row => {
    const id = row.getAttribute('data-id');
    if (!byId.has(id)) { if (!busy(row)) row.remove(); return; }
    const entry = byId.get(id);
    const sig = JSON.stringify(entry) + (newestFirst && id === latestId ? '|latest' : '');
    const was = row.dataset.liveSig || '';
    if (was !== sig && !busy(row)) {
      const fresh = build(entry, id === latestId);
      fresh.dataset.liveSig = sig;
      row.replaceWith(fresh);
      // Glow a real change, not just the "Latest" chip moving to a newer entry.
      if (was.replace('|latest', '') !== sig.replace('|latest', '')) changed.push(fresh);
    }
    byId.delete(id);
  });
  byId.forEach(entry => {
    const id = String(entry.ID);
    const fresh = build(entry, id === latestId);
    fresh.dataset.liveSig = JSON.stringify(entry) + (newestFirst && id === latestId ? '|latest' : '');
    const t = new Date(entry.Time_Submitted).getTime();
    const before = [...container.querySelectorAll(`.${rowClass}`)].find(row => {
      const other = new Date(row.dataset.timeSubmitted).getTime();
      return newestFirst ? other < t : other > t;
    });
    container.insertBefore(fresh, before || null);
    changed.push(fresh);
  });
  return changed;
}

export function refreshOpenLaborParts(job) {
  if (document.getElementById('statusTable')?.style.display !== 'flex') return;
  if (!job || document.getElementById('currentJobId')?.value !== job.Job_ID) return;
  const labor = syncEntryRows(document.getElementById('labor-table-body-current'), Object.values(job.Labor || {}), 'labor-entry', (entry, isLatest) => {
    const div = document.createElement('div');
    appendLaborDataToDiv(div, entry, isLatest);
    return div;
  }, true);
  const parts = syncEntryRows(document.getElementById('parts-table-body-current'), Object.values(job.Parts || {}), 'part-entry', (entry) => {
    const div = document.createElement('div');
    appendPartsDataToDiv(div, entry);
    return div;
  }, false);
  if (labor.length) updateLaborCount();
  if (parts.length) updatePartsCount();
  shell.glowJobRows([...labor, ...parts].map(el => { el.dataset.key = el.getAttribute('data-id') || 'entry'; return el; }));
}

export async function generateLaborTable(id) {
  try {
    const laborContainer = document.getElementById('labor-table-body-current');
    laborContainer.innerHTML = '';
    bindLaborTableDelegationOnce();

    const db = await getDB();
    const transaction = db.transaction(['jobData'], 'readonly');
    const objectStore = transaction.objectStore('jobData');

    const job = await new Promise((resolve, reject) => {
      const req = objectStore.get(id);
      req.onerror = (event) => {
        console.error("Error fetching labor data: ", event.target.error);
        reject(event.target.error);
      };
      req.onsuccess = () => resolve(req.result || null);
    });

    if (job && job.Labor) {
      const laborEntries = Object.values(job.Labor);

      // Newest first — the newest entry gets the "Latest" chip.
      laborEntries.sort((a, b) => new Date(b.Time_Submitted) - new Date(a.Time_Submitted));

      const frag = document.createDocumentFragment();

      laborEntries.forEach((laborEntry, i) => {
        const laborDiv = document.createElement('div');
        laborDiv.className = 'labor-entry';
        appendLaborDataToDiv(laborDiv, laborEntry, i === 0);
        laborDiv.dataset.liveSig = JSON.stringify(laborEntry) + (i === 0 ? '|latest' : '');
        frag.appendChild(laborDiv);
      });

      laborContainer.replaceChildren(frag); // not append: a live update may have slotted rows in meanwhile

      // Clamp long narratives to 4 lines with a "Show all" toggle, measured
      // now that the rows have real layout.
      laborContainer.querySelectorAll('.labor-data.description').forEach(descEl => {
        descEl.classList.add('clamped');
        requestAnimationFrame(() => {
          const showAllBtn = descEl.parentElement.querySelector('.labor-entry-show-all');
          if (descEl.scrollHeight > descEl.clientHeight + 1) {
            if (showAllBtn) showAllBtn.style.display = '';
          } else {
            descEl.classList.remove('clamped');
          }
        });
      });
    } else {
      console.log("No matching labor data found.");
    }

    updateLaborCount();
  } catch (error) {
    console.error("Error fetching labor data: ", error);
  }
}

const laborSaveTimers = new Map();
const laborHoldDelayType = 3000;

function bindLaborTableDelegationOnce() {
  const container = document.getElementById('labor-table-body-current');
  if (!container) return;

  if (container.dataset.laborBound === "1") return;
  container.dataset.laborBound = "1";

  container.addEventListener('click', (e) => {
    const btn = e.target.closest('.labor-data-delete-button');
    if (!btn) return;

    // Ensure deleteLabor can find the correct row via event target
    deleteLabor(e);
  });

  container.addEventListener('input', (e) => {
    const row = e.target.closest('.labor-entry');
    if (!row) return;

    const id = row.getAttribute('data-id');
    if (!id) return;

    if (laborSaveTimers.has(id)) clearTimeout(laborSaveTimers.get(id));

    const t = setTimeout(async () => {
      try {
        const tech = row.querySelector('.labor-tech .labor-data')?.textContent || '';
        const desc = row.querySelector('.labor-data.description')?.textContent || '';
        const hours = row.querySelector('.labor-data.hours')?.textContent || '';

        // ✅ Use the real stored Time_Submitted, not the MM/DD display text
        let timeSubmitted = row.dataset.timeSubmitted || '';

        // If it was missing, assign today's date ONCE and persist it on the row
        if (!timeSubmitted) {
          timeSubmitted = await getTodaysDate();
          row.dataset.timeSubmitted = timeSubmitted;

          // Optional: update the displayed MM/DD so UI matches
          const dateP = row.querySelector('.labor-data.date');
          if (dateP) {
            const parts = String(timeSubmitted).split('-');
            dateP.textContent = (parts[1] && parts[2]) ? `${parts[1]}/${parts[2]}` : timeSubmitted;
          }
        }

        const laborData = {
          Tech: tech,
          Description: desc,
          Hours: hours,
          Category: document.getElementById('currentCategory')?.value || '',
          Op_Type: document.getElementById('currentOpType')?.value || '',
          Job_ID: document.getElementById('currentJobId')?.value || '',
          ID: id,
          Boat_Name: document.getElementById('currentBoat')?.value || '',
          Customer_Name: document.getElementById('currentName')?.value || '',
          Time_Submitted: timeSubmitted,
          Work_Order: document.getElementById('currentWorkOrder')?.value || ''
        };

        addLaborFirebase(laborData);
      } catch (err) {
        console.error('Labor save error:', err);
      } finally {
        laborSaveTimers.delete(id);
      }
    }, laborHoldDelayType);

    laborSaveTimers.set(id, t);
  });
}

function appendLaborDataToDiv(laborDiv, laborEntry, isLatest) {
  laborDiv.className = 'labor-entry';

  const id = laborEntry?.ID;
  laborDiv.setAttribute('data-id', id || '');

  laborDiv.dataset.timeSubmitted = laborEntry?.Time_Submitted || '';

  const topRow = document.createElement('div');
  topRow.className = 'labor-entry-top';

  const tech = laborEntry?.Tech || '';
  const avatar = document.createElement('div');
  avatar.className = 'labor-entry-avatar';
  avatar.textContent = tech.trim().charAt(0).toUpperCase();

  const techDiv = document.createElement('div');
  techDiv.className = 'labor-tech';

  const techP = document.createElement('p');
  techP.className = 'labor-data';
  techP.contentEditable = true;
  techP.textContent = tech;
  techDiv.appendChild(techP);

  const dateP = document.createElement('span');
  dateP.className = 'labor-data date';
  dateP.contentEditable = true;

  const ts = laborEntry?.Time_Submitted || '';
  if (ts) {
    const parts = String(ts).split('-');
    dateP.textContent = (parts[1] && parts[2]) ? `${parts[1]}/${parts[2]}` : ts;
  } else {
    dateP.textContent = '';
  }

  const latestChip = document.createElement('span');
  latestChip.className = 'labor-entry-latest-chip';
  latestChip.textContent = 'Latest';
  if (!isLatest) latestChip.style.display = 'none';

  const hoursWrap = document.createElement('span');
  hoursWrap.className = 'labor-hours-wrap';
  const hoursP = document.createElement('span');
  hoursP.className = 'labor-data hours';
  hoursP.contentEditable = true;
  hoursP.textContent = laborEntry?.Hours || '';
  const hoursUnit = document.createElement('span');
  hoursUnit.className = 'labor-data hours-unit';
  hoursUnit.textContent = ' hrs';
  hoursWrap.appendChild(hoursP);
  hoursWrap.appendChild(hoursUnit);

  topRow.appendChild(avatar);
  topRow.appendChild(techDiv);
  topRow.appendChild(dateP);
  topRow.appendChild(latestChip);
  topRow.appendChild(hoursWrap);

  const descriptionP = document.createElement('p');
  descriptionP.className = 'labor-data description';
  descriptionP.contentEditable = true;
  descriptionP.textContent = laborEntry?.Description || '';

  const showAllBtn = document.createElement('button');
  showAllBtn.type = 'button';
  showAllBtn.className = 'labor-entry-show-all';
  showAllBtn.textContent = 'Show all';
  showAllBtn.style.display = 'none';
  showAllBtn.addEventListener('click', () => {
    const expanding = descriptionP.classList.contains('clamped');
    descriptionP.classList.toggle('clamped');
    showAllBtn.textContent = expanding ? 'Show less' : 'Show all';
  });

  const deleteDiv = document.createElement('div');
  deleteDiv.className = 'labor-part-delete-div';

  const deleteButton = document.createElement('button');
  deleteButton.className = 'labor-data-delete-button table-button';
  deleteButton.type = 'button';
  deleteButton.innerHTML = '<img src="/assets/close_icon.png" alt="Close Icon" width="24" height="24">';
  deleteDiv.appendChild(deleteButton);

  laborDiv.appendChild(topRow);
  laborDiv.appendChild(descriptionP);
  laborDiv.appendChild(showAllBtn);
  laborDiv.appendChild(deleteDiv);
}



async function generatePartsTable(id) {
  try {
    const partsContainer = document.getElementById('parts-table-body-current');
    partsContainer.innerHTML = '';

    const db = await getDB();
    const transaction = db.transaction(['jobData'], 'readonly');
    const objectStore = transaction.objectStore('jobData');

    const job = await new Promise((resolve, reject) => {
      const req = objectStore.get(id);
      req.onerror = (event) => {
        console.error("Error fetching parts data: ", event.target.error);
        reject(event.target.error);
      };
      req.onsuccess = () => resolve(req.result || null);
    });

    if (job && job.Parts) {
      const partEntries = Object.values(job.Parts);

      partEntries.sort((a, b) => new Date(a.Time_Submitted) - new Date(b.Time_Submitted));

      const frag = document.createDocumentFragment();

      partEntries.forEach(partEntry => {
        const partDiv = document.createElement('div');
        partDiv.setAttribute('data-id', partEntry.ID);
        partDiv.className = 'part-entry';

        appendPartsDataToDiv(partDiv, partEntry);
        partDiv.dataset.liveSig = JSON.stringify(partEntry);

        frag.appendChild(partDiv);
      });

      partsContainer.replaceChildren(frag); // not append: a live update may have slotted rows in meanwhile
    }

    updatePartsCount();
  } catch (error) {
    console.error("Error fetching parts data: ", error);
  }
}

// Parts logged without a real inventory part number are typed straight into
// the part-number field today (e.g. "electrical connectors $80") — flag those
// as Misc instead of rendering a fake mono part number.
export function isMiscPartNumber(partNumber) {
  return /\$/.test(partNumber || '');
}


function appendPartsDataToDiv(partDiv, partEntry) {
  partDiv.className = 'part-entry';
  partDiv.setAttribute('data-id', partEntry.ID);
  partDiv.dataset.timeSubmitted = partEntry.Time_Submitted || '';

  const techDiv = document.createElement('div');
  techDiv.className = 'part-tech';
  techDiv.innerHTML = `
    <p class="part-data tech" contenteditable="true">${escapeHTML(partEntry.Tech || '')}</p>
  `;

  const partNumberDiv = document.createElement('div');
  partNumberDiv.className = 'part-table-part-number';

  const partNumberRow = document.createElement('div');
  partNumberRow.className = 'part-table-part-number-row';

  const partNumberP = document.createElement('p');
  partNumberP.className = 'part-table-part-data';
  partNumberP.contentEditable = true;
  partNumberP.textContent = partEntry.Part_Number || '';
  partNumberRow.appendChild(partNumberP);

  const miscBadge = document.createElement('span');
  miscBadge.className = 'part-misc-badge';
  miscBadge.textContent = 'Misc';
  miscBadge.style.display = isMiscPartNumber(partEntry.Part_Number) ? '' : 'none';
  partNumberRow.appendChild(miscBadge);

  partNumberDiv.appendChild(partNumberRow);

  const descriptionP = document.createElement('p');
  descriptionP.className = 'part-data part-description';
  descriptionP.textContent = partEntry.Description || '';
  partNumberDiv.appendChild(descriptionP);

  // Keep the Misc badge in sync as the tech edits the part-number field.
  partNumberP.addEventListener('input', () => {
    miscBadge.style.display = isMiscPartNumber(partNumberP.textContent) ? '' : 'none';
  });

  const quantityP = document.createElement('p');
  quantityP.className = 'part-data quantity';
  quantityP.contentEditable = true;
  quantityP.textContent = partEntry.Quantity || '';

  const dateP = document.createElement('p');
  dateP.className = 'part-data date';

  const ts = partEntry.Time_Submitted || '';
  if (ts) {
    const parts = String(ts).split('-');
    dateP.textContent = (parts[1] && parts[2]) ? `${parts[1]}/${parts[2]}` : ts;
  } else {
    dateP.textContent = '';
  }

  const deleteDiv = document.createElement('div');
  deleteDiv.className = 'labor-part-delete-div';

  const deleteButton = document.createElement('button');
  deleteButton.className = 'parts-data-delete-button table-button';
  deleteButton.type = 'button';
  deleteButton.innerHTML = '<img src="/assets/close_icon.png" alt="Close Icon" width="24" height="24">';
  deleteButton.addEventListener('click', deleteParts);

  deleteDiv.appendChild(deleteButton);

  partDiv.appendChild(techDiv);
  partDiv.appendChild(partNumberDiv);
  partDiv.appendChild(quantityP);
  partDiv.appendChild(dateP);
  partDiv.appendChild(deleteDiv);

  let holdTimer;
  let holdDelayType = 3000;

  partDiv.addEventListener('input', () => {
    clearTimeout(holdTimer);
  });

  partDiv.addEventListener('keyup', () => {
    clearTimeout(holdTimer);

    holdTimer = setTimeout(async () => {
      const jobId = document.getElementById('currentJobId').value;

      let timeSubmitted = partDiv.dataset.timeSubmitted || '';
      if (!timeSubmitted) {
        timeSubmitted = await getTodaysDate();
        partDiv.dataset.timeSubmitted = timeSubmitted;

        const p = String(timeSubmitted).split('-');
        dateP.textContent = (p[1] && p[2]) ? `${p[1]}/${p[2]}` : timeSubmitted;
      }

      const partData = {
        Part_Number: partNumberP.textContent,
        Quantity: quantityP.textContent,
        Category: document.getElementById('currentCategory').value,
        Op_Type: document.getElementById('currentOpType').value,
        Job_ID: jobId,
        ID: partEntry.ID,
        Boat_Name: document.getElementById('currentBoat').value,
        Customer_Name: document.getElementById('currentName').value,
        Time_Submitted: timeSubmitted,
        Work_Order: document.getElementById('currentWorkOrder').value,
        Tech: partDiv.querySelector('.part-data.tech').textContent,
        Description: descriptionP.textContent
      };

      addPartsFirebase(partData);
      updatePartsIndex(jobId, partData);
      updateTimestamps('jobData', 'data');
    }, holdDelayType);
  });
}
